import React, { useEffect, useMemo, useState } from "react";

type ClassStatus = "Live Now" | "Upcoming" | "Recorded";

type ClassCategory =
  | "All"
  | "Foundation"
  | "JEE"
  | "NEET"
  | "Boards"
  | "Doubt Class";

type LiveClass = {
  id: number;
  title: string;
  seoTitle: string;
  category: Exclude<ClassCategory, "All">;
  subject: string;
  mentor: string;
  time: string;
  date: string;
  duration: string;
  status: ClassStatus;
  students: string;
  level: string;
  image: string;
  imageAlt: string;
  points: string[];
  keywords: string;
};

type ProcessStep = {
  count: string;
  title: string;
  desc: string;
  keywords: string;
};

type Feature = {
  title: string;
  desc: string;
  keywords: string;
};

const categories: ClassCategory[] = [
  "All",
  "Foundation",
  "JEE",
  "NEET",
  "Boards",
  "Doubt Class",
];

const categorySeoText: Record<ClassCategory, string> = {
  All: "All live online classes for school foundation, CBSE board preparation, JEE Main, NEET UG, doubt solving, recorded lectures and test practice.",
  Foundation:
    "Foundation live classes for Class 7, Class 8, Class 9 and Class 10 students with Maths, Science, reasoning, worksheets and concept revision.",
  JEE: "JEE live classes for Physics, Chemistry and Mathematics with JEE Main practice, DPP discussion, PYQ solving and performance tracking.",
  NEET: "NEET live classes for Physics, Chemistry and Biology with NCERT revision, Biology diagrams, MCQ practice and medical entrance preparation.",
  Boards:
    "Board exam live classes for CBSE, NCERT, Class 10, Class 12, Science, Maths, English grammar, revision classes and sample paper practice.",
  "Doubt Class":
    "Live doubt solving classes for JEE, NEET, CBSE, Maths, Science, Physics, Chemistry and Biology with exam strategy and question discussion.",
};

const liveClasses: LiveClass[] = [
  {
    id: 1,
    title: "Class 10 Science: Chemical Reactions",
    seoTitle:
      "Class 10 Science Chemical Reactions Live Class for CBSE Board Preparation",
    category: "Boards",
    subject: "Science",
    mentor: "Dr. Ananya Sharma",
    time: "06:00 PM",
    date: "Today",
    duration: "60 min",
    status: "Live Now",
    students: "1.2k",
    level: "Board Booster",
    image:
      "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "Class 10 Science live class for chemical reactions CBSE board preparation and NCERT concepts",
    points: ["NCERT concepts", "Important questions", "Live doubt support"],
    keywords:
      "Class 10 Science live class, Chemical Reactions, CBSE board preparation, NCERT Science, Class 10 important questions, Science doubt solving",
  },
  {
    id: 2,
    title: "JEE Main Physics: Laws of Motion",
    seoTitle:
      "JEE Main Physics Laws of Motion Live Class with Numericals and PYQ Practice",
    category: "JEE",
    subject: "Physics",
    mentor: "Amit Verma",
    time: "07:30 PM",
    date: "Today",
    duration: "75 min",
    status: "Upcoming",
    students: "2.5k",
    level: "JEE Target",
    image:
      "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "JEE Main Physics live class for Laws of Motion numericals and previous year questions",
    points: ["Concept + numericals", "JEE PYQ practice", "DPP discussion"],
    keywords:
      "JEE Main Physics live class, Laws of Motion, JEE numericals, Physics PYQ, JEE DPP discussion, IIT JEE preparation",
  },
  {
    id: 3,
    title: "NEET Biology: Human Physiology",
    seoTitle:
      "NEET Biology Human Physiology Live Class with NCERT Revision and MCQ Practice",
    category: "NEET",
    subject: "Biology",
    mentor: "Neha Singh",
    time: "08:15 PM",
    date: "Today",
    duration: "70 min",
    status: "Upcoming",
    students: "3.1k",
    level: "NEET Masterclass",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "NEET Biology live class for Human Physiology NCERT revision diagrams and MCQ practice",
    points: ["NCERT line-by-line", "Diagrams", "Rapid MCQ practice"],
    keywords:
      "NEET Biology live class, Human Physiology, NCERT Biology revision, NEET MCQ practice, Biology diagrams, medical entrance coaching",
  },
  {
    id: 4,
    title: "Class 8 Foundation: Maths Practice",
    seoTitle:
      "Class 8 Maths Foundation Live Class for Practice Worksheets and Concept Revision",
    category: "Foundation",
    subject: "Maths",
    mentor: "Rohit Mishra",
    time: "05:00 PM",
    date: "Tomorrow",
    duration: "55 min",
    status: "Upcoming",
    students: "850+",
    level: "Foundation",
    image:
      "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "Class 8 Maths foundation live class for worksheet solving concept revision and practice",
    points: ["Basic to advanced", "Worksheet solving", "Concept revision"],
    keywords:
      "Class 8 Maths live class, Class 8 online tuition, Maths foundation, worksheet solving, concept revision, school foundation course",
  },
  {
    id: 5,
    title: "Board Revision: English Grammar",
    seoTitle:
      "CBSE Board English Grammar Live Revision Class with Writing Practice",
    category: "Boards",
    subject: "English",
    mentor: "Priya Kapoor",
    time: "04:30 PM",
    date: "Tomorrow",
    duration: "50 min",
    status: "Upcoming",
    students: "720+",
    level: "Boards",
    image:
      "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "CBSE English grammar live revision class for board exam writing practice and questions",
    points: ["Grammar rules", "Writing practice", "Board-style questions"],
    keywords:
      "English Grammar live class, CBSE board English, writing practice, board exam revision, English grammar rules, board style questions",
  },
  {
    id: 6,
    title: "Live Doubt Solving: JEE + NEET",
    seoTitle:
      "Live Doubt Solving Class for JEE NEET Physics Chemistry Maths and Biology",
    category: "Doubt Class",
    subject: "Doubt Solving",
    mentor: "Expert Faculty Team",
    time: "09:00 PM",
    date: "Today",
    duration: "45 min",
    status: "Live Now",
    students: "1.8k",
    level: "Doubt Room",
    image:
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "Live doubt solving class for JEE NEET Physics Chemistry Maths Biology and exam strategy",
    points: ["Ask live doubts", "Question discussion", "Exam strategy"],
    keywords:
      "live doubt solving, JEE doubts, NEET doubts, Physics doubts, Chemistry doubts, Maths doubts, Biology doubts, exam strategy",
  },
  {
    id: 7,
    title: "NEET Chemistry: Organic Basics",
    seoTitle:
      "NEET Chemistry Organic Basics Recorded Class for Reaction Practice",
    category: "NEET",
    subject: "Chemistry",
    mentor: "Vikas Jain",
    time: "Recorded",
    date: "Yesterday",
    duration: "65 min",
    status: "Recorded",
    students: "4.4k",
    level: "Recorded",
    image:
      "https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "NEET Chemistry recorded class for organic chemistry basics reactions and practice questions",
    points: ["Organic foundation", "Reaction basics", "Practice questions"],
    keywords:
      "NEET Chemistry recorded class, Organic Chemistry basics, Chemistry reactions, NEET Chemistry practice, medical entrance Chemistry",
  },
  {
    id: 8,
    title: "Class 9 Foundation: Science Revision",
    seoTitle:
      "Class 9 Science Foundation Recorded Class for Chapter Summary and Quick Test Practice",
    category: "Foundation",
    subject: "Science",
    mentor: "Sakshi Gupta",
    time: "Recorded",
    date: "Yesterday",
    duration: "58 min",
    status: "Recorded",
    students: "2.2k",
    level: "Recorded",
    image:
      "https://images.unsplash.com/photo-1523580846011-d3a5bc25702b?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "Class 9 Science recorded foundation class for chapter summary definitions and test practice",
    points: ["Chapter summary", "Key definitions", "Quick test practice"],
    keywords:
      "Class 9 Science recorded class, Science revision, chapter summary, key definitions, Class 9 foundation, quick test practice",
  },
];

const processSteps: ProcessStep[] = [
  {
    count: "01",
    title: "Login or Register",
    desc: "Students can login with registered details and access enrolled live batches, course dashboard, class schedule and learning resources.",
    keywords:
      "student login, online class registration, live batch access, student dashboard, enrolled courses",
  },
  {
    count: "02",
    title: "Choose Live Class",
    desc: "Students can select a live class according to class, subject, course category, exam target and batch schedule.",
    keywords:
      "choose live class, class schedule, subject wise live class, JEE live batch, NEET live batch, board class schedule",
  },
  {
    count: "03",
    title: "Join and Ask Doubts",
    desc: "During live classes, students can learn from mentors, ask doubts, discuss questions and improve concept clarity.",
    keywords:
      "join live class, live doubt solving, mentor guidance, online tuition class, concept clarity",
  },
  {
    count: "04",
    title: "Get Recording and Test",
    desc: "After class, students can revise with recordings, PDFs, DPPs, quizzes and performance reports from the dashboard.",
    keywords:
      "recorded lectures, PDF notes, DPP, online quiz, performance report, class recording",
  },
];

const features: Feature[] = [
  {
    title: "HD Live Classes with Expert Mentors",
    desc: "Students learn directly from subject mentors through interactive live online classes.",
    keywords:
      "HD live classes, expert mentors, online teachers, live online learning",
  },
  {
    title: "Class-Wise Schedule and Reminders",
    desc: "Students can track upcoming classes, timings, subjects and batch reminders.",
    keywords:
      "class schedule, live class reminder, batch timetable, upcoming online classes",
  },
  {
    title: "Live Chat and Doubt Solving Support",
    desc: "Students can ask subject doubts and understand concepts with guided support.",
    keywords:
      "live chat, doubt solving, Maths doubts, Physics doubts, Chemistry doubts, Biology doubts",
  },
  {
    title: "PDF Notes, DPP and Assignments",
    desc: "Students can revise after class using notes, worksheets, assignments and practice problems.",
    keywords:
      "PDF notes, DPP, assignments, worksheets, study material, practice problems",
  },
  {
    title: "Recorded Lectures for Revision",
    desc: "Recorded classes help students revise missed or difficult topics at their own pace.",
    keywords:
      "recorded lectures, recorded classes, revision videos, missed class support",
  },
  {
    title: "Quiz, Mock Test and Progress Tracking",
    desc: "Regular tests and reports help students understand performance and improvement areas.",
    keywords:
      "online quiz, mock test, progress tracking, performance report, score analysis",
  },
];

const seoKeywords = [
  "Mediit Academy live batches",
  "live online classes",
  "online classes for students",
  "Class 7 live classes",
  "Class 8 online tuition",
  "Class 9 Science live class",
  "Class 10 board live classes",
  "CBSE online classes",
  "NCERT live learning",
  "JEE live classes",
  "JEE Main online coaching",
  "NEET live classes",
  "NEET UG online coaching",
  "Physics live class",
  "Chemistry live class",
  "Biology live class",
  "Maths live class",
  "live doubt solving",
  "recorded lectures",
  "PDF notes",
  "DPP practice",
  "online mock tests",
  "student dashboard",
  "performance tracking",
].join(", ");

const getStatusStyle = (status: ClassStatus) => {
  if (status === "Live Now") {
    return "border-red-200 bg-red-50 text-red-600";
  }

  if (status === "Upcoming") {
    return "border-blue-200 bg-blue-50 text-blue-600";
  }

  return "border-slate-200 bg-slate-50 text-slate-600";
};

const getButtonText = (status: ClassStatus) => {
  if (status === "Live Now") return "Join Live Class";
  if (status === "Upcoming") return "Set Reminder";
  return "Watch Recording";
};

const LiveBatches: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<ClassCategory>("All");

  const filteredClasses = useMemo(() => {
    if (activeCategory === "All") return liveClasses;
    return liveClasses.filter((item) => item.category === activeCategory);
  }, [activeCategory]);

  const liveNowCount = liveClasses.filter(
    (item) => item.status === "Live Now"
  ).length;

  const upcomingCount = liveClasses.filter(
    (item) => item.status === "Upcoming"
  ).length;

  const recordedCount = liveClasses.filter(
    (item) => item.status === "Recorded"
  ).length;

  useEffect(() => {
    const title =
      activeCategory === "All"
        ? "Live Batches | Online Live Classes for JEE, NEET, CBSE and Foundation - Mediit Academy"
        : `${activeCategory} Live Classes | Online Live Batches - Mediit Academy`;

    const description =
      activeCategory === "All"
        ? "Join Mediit Academy live online classes for Foundation, CBSE boards, JEE Main, NEET UG and doubt solving with expert mentors, PDF notes, DPPs, recordings, quizzes and performance tracking."
        : categorySeoText[activeCategory];

    const keywords = [
      seoKeywords,
      categorySeoText[activeCategory],
      `${activeCategory} live classes`,
      `${activeCategory} online classes`,
      `${activeCategory} live batch`,
    ].join(", ");

    document.title = title;

    const setMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    const setPropertyTag = (property: string, content: string) => {
      let tag = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMetaTag("description", description);
    setMetaTag("keywords", keywords);
    setMetaTag("robots", "index, follow");
    setMetaTag("author", "Mediit Academy");

    setPropertyTag("og:title", title);
    setPropertyTag("og:description", description);
    setPropertyTag("og:type", "website");
    setPropertyTag("og:site_name", "Mediit Academy");

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);

    let canonical = document.querySelector(
      `link[rel="canonical"]`
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", `${window.location.origin}/live-batches`);
  }, [activeCategory]);

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Mediit Academy Live Batches",
    description:
      "Live online classes for Foundation, Boards, JEE, NEET and doubt solving with recordings, notes, DPPs, quizzes and performance tracking.",
    url: "/live-batches",
    keywords: seoKeywords,
    mainEntity: {
      "@type": "ItemList",
      name: "Live Classes and Recorded Batches",
      itemListElement: liveClasses.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Course",
          name: item.seoTitle,
          description: `${item.title} by ${item.mentor}. ${item.points.join(
            ", "
          )}.`,
          educationalLevel: item.level,
          courseMode: "online",
          provider: {
            "@type": "EducationalOrganization",
            name: "Mediit Academy",
            url: "/",
          },
          keywords: item.keywords,
        },
      })),
    },
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How Students Join Live Classes at Mediit Academy",
    description:
      "Simple process to login, choose a live class, join the session, ask doubts and revise with recordings, notes and tests.",
    step: processSteps.map((step) => ({
      "@type": "HowToStep",
      name: step.title,
      text: step.desc,
    })),
  };

  const goToLogin = () => {
    window.location.href = "/login";
  };

  const scrollToSchedule = () => {
    const schedule = document.getElementById("live-schedule");
    schedule?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#f8fbff] font-['Inter'] text-[#101827]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(pageSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(howToSchema),
        }}
      />

      <main>
        {/* HERO */}
        <section
          className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,#dbeafe_0%,transparent_34%),linear-gradient(135deg,#f8fbff_0%,#ffffff_45%,#eef5ff_100%)]"
          aria-labelledby="live-batches-heading"
        >
          <div className="absolute left-0 top-24 h-64 w-64 rounded-full bg-blue-200/40 blur-3xl" />
          <div className="absolute bottom-10 right-0 h-72 w-72 rounded-full bg-orange-200/40 blur-3xl" />
          <div className="absolute right-[24%] top-20 hidden h-28 w-28 rounded-full border border-blue-200 lg:block" />

          <div className="relative mx-auto max-w-[1380px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 shadow-sm">
                  <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
                  </span>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                    Mediit Live Learning
                  </p>
                </div>

                <h1
                  id="live-batches-heading"
                  className="mt-5 max-w-4xl text-4xl font-black leading-tight tracking-[-0.05em] text-slate-950 sm:text-5xl lg:text-[64px]"
                >
                  Live Online Classes, Doubt Solving and Recordings in One Smart
                  Dashboard
                </h1>

                <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                  Join daily live classes for Foundation, CBSE boards, JEE Main,
                  NEET UG and doubt solving. Students get expert mentors, PDF
                  notes, DPPs, quizzes, recorded lectures and performance
                  tracking inside one learning dashboard.
                </p>

                <p className="sr-only">{seoKeywords}</p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={goToLogin}
                    aria-label="Join Mediit Academy live online class"
                    className="rounded-2xl bg-[#101827] px-7 py-4 text-sm font-black text-white shadow-xl shadow-slate-900/15 transition-all hover:-translate-y-0.5 hover:bg-slate-800"
                  >
                    Join Live Class
                  </button>

                  <button
                    type="button"
                    onClick={scrollToSchedule}
                    aria-label="View live class schedule"
                    className="rounded-2xl border border-slate-200 bg-white px-7 py-4 text-sm font-black text-slate-950 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-400"
                  >
                    View Schedule
                  </button>
                </div>

                <div
                  className="mt-9 grid max-w-2xl grid-cols-3 gap-3"
                  aria-label="Live class statistics"
                >
                  <div className="rounded-3xl border border-red-100 bg-white p-4 text-center shadow-sm">
                    <p className="text-3xl font-black text-red-600">
                      {liveNowCount}
                    </p>
                    <p className="mt-1 text-xs font-bold text-slate-500">
                      Live Now
                    </p>
                  </div>

                  <div className="rounded-3xl border border-blue-100 bg-white p-4 text-center shadow-sm">
                    <p className="text-3xl font-black text-blue-600">
                      {upcomingCount}
                    </p>
                    <p className="mt-1 text-xs font-bold text-slate-500">
                      Upcoming
                    </p>
                  </div>

                  <div className="rounded-3xl border border-slate-100 bg-white p-4 text-center shadow-sm">
                    <p className="text-3xl font-black text-slate-950">
                      {recordedCount}
                    </p>
                    <p className="mt-1 text-xs font-bold text-slate-500">
                      Recordings
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="relative mx-auto max-w-[520px]">
                  <div className="absolute -right-4 bottom-20 z-10 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-xl md:block">
                    <p className="text-xs font-bold text-slate-500">
                      Today Classes
                    </p>
                    <p className="mt-1 text-2xl font-black text-blue-600">08</p>
                  </div>

                  <div className="overflow-hidden">
                    <div className="relative">
                      <img
                        src="https://www.neverskip.com/wp-content/uploads/2023/06/Hub-of-Blending-Learning.png"
                        alt="Live online class dashboard with notes quiz recordings and student learning support"
                        loading="eager"
                        decoding="async"
                        className="h-[300px] w-full object-cover opacity-90 sm:h-[350px]"
                      />

                      <div className="absolute left-5 top-5 rounded-full bg-red-600 px-4 py-2 text-xs font-black text-white shadow-lg">
                        ● LIVE
                      </div>

                      <div className="absolute -bottom-16 left-5 right-5 rounded-3xl border border-white/10 bg-white/10 p-5 text-white backdrop-blur-md">
                        <p className="text-xs font-black uppercase tracking-[0.16em] text-black">
                          Today 06:00 PM
                        </p>
                        <h2 className="mt-2 text-2xl font-black leading-tight text-black">
                          Science: Chemical Reactions
                        </h2>
                        <p className="mt-2 text-sm leading-6 text-black">
                          Join the class, ask doubts live, get notes and
                          practice questions after the session.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 divide-x divide-white/10 p-5 text-center text-white">
                      <div>
                        <p className="text-xl font-black">HD</p>
                        <p className="text-xs text-white/55">Class</p>
                      </div>
                      <div>
                        <p className="text-xl font-black">PDF</p>
                        <p className="text-xs text-white/55">Notes</p>
                      </div>
                      <div>
                        <p className="text-xl font-black">Quiz</p>
                        <p className="text-xs text-white/55">Practice</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section className="bg-white py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-9 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  How Live Classes Work
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Simple Learning Process for Students
                </h2>
              </div>

              <p className="max-w-xl text-sm leading-6 text-slate-500">
                Students can easily understand how to join a class, ask doubts,
                download notes and revise with recordings, DPPs, quizzes and
                reports after the session.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {processSteps.map((step) => (
                <article
                  key={step.count}
                  title={step.keywords}
                  className="group rounded-[28px] border border-slate-100 bg-[#f8fbff] p-6 transition-all hover:-translate-y-1 hover:bg-white hover:shadow-xl"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#101827] text-lg font-black text-white transition-all group-hover:bg-blue-600">
                    {step.count}
                  </div>
                  <h3 className="text-xl font-black text-slate-950">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    {step.desc}
                  </p>
                  <p className="sr-only">{step.keywords}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* SCHEDULE */}
        <section id="live-schedule" className="bg-[#f8fbff] py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Class Schedule
                </p>

                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Live, Upcoming and Recorded Classes
                </h2>
              </div>

              <p className="max-w-xl text-sm leading-6 text-slate-500">
                Select a category to filter Foundation, JEE, NEET, Boards and
                Doubt Classes instantly according to your learning goal.
              </p>
            </div>

            <div
              className="mb-8 flex gap-3 overflow-x-auto pb-2"
              aria-label="Live class category filters"
            >
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  title={categorySeoText[category]}
                  aria-label={`Show ${category} live classes. ${categorySeoText[category]}`}
                  className={`shrink-0 rounded-full border px-5 py-2.5 text-sm font-black transition-all ${
                    activeCategory === category
                      ? "border-[#101827] bg-[#101827] text-white shadow-lg shadow-slate-900/15"
                      : "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-600"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <p className="mb-6 text-sm leading-6 text-slate-500">
              {categorySeoText[activeCategory]}
            </p>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filteredClasses.map((item) => (
                <article
                  key={item.id}
                  title={item.keywords}
                  className="group overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:border-blue-200 hover:shadow-[0_22px_60px_rgba(15,23,42,0.12)]"
                >
                  <div className="relative h-[220px] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.imageAlt}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/10 to-transparent" />

                    <span
                      className={`absolute left-4 top-4 rounded-full border px-3 py-1 text-xs font-black ${getStatusStyle(
                        item.status
                      )}`}
                    >
                      {item.status}
                    </span>

                    <span className="absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-black text-slate-950 backdrop-blur">
                      {item.level}
                    </span>

                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-blue-600 shadow-sm">
                        {item.category}
                      </span>
                      <h3 className="mt-3 text-2xl font-black leading-tight tracking-[-0.03em] text-white">
                        {item.title}
                      </h3>
                    </div>
                  </div>

                  <div className="p-5">
                    <p className="text-sm font-bold text-blue-700">
                      {item.seoTitle}
                    </p>

                    <div className="mb-4 mt-3 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-black uppercase tracking-[0.14em] text-blue-600">
                          {item.subject}
                        </p>
                        <p className="mt-1 text-sm font-bold text-slate-500">
                          Mentor:{" "}
                          <span className="text-slate-950">{item.mentor}</span>
                        </p>
                      </div>

                      <div className="rounded-2xl bg-slate-50 px-3 py-2 text-right">
                        <p className="text-sm font-black text-slate-950">
                          {item.students}
                        </p>
                        <p className="text-[11px] font-bold text-slate-500">
                          Students
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-2xl bg-slate-50 p-3">
                        <p className="text-xs text-slate-500">Date</p>
                        <p className="mt-1 text-sm font-black text-slate-950">
                          {item.date}
                        </p>
                      </div>

                      <div className="rounded-2xl bg-slate-50 p-3">
                        <p className="text-xs text-slate-500">Time</p>
                        <p className="mt-1 text-sm font-black text-slate-950">
                          {item.time}
                        </p>
                      </div>

                      <div className="rounded-2xl bg-slate-50 p-3">
                        <p className="text-xs text-slate-500">Duration</p>
                        <p className="mt-1 text-sm font-black text-slate-950">
                          {item.duration}
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 space-y-2">
                      {item.points.map((point) => (
                        <div key={point} className="flex items-center gap-2">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-50 text-xs font-black text-green-600">
                            ✓
                          </span>
                          <p className="text-sm font-medium text-slate-700">
                            {point}
                          </p>
                        </div>
                      ))}
                    </div>

                    <p className="sr-only">{item.keywords}</p>

                    <button
                      type="button"
                      onClick={goToLogin}
                      aria-label={`${getButtonText(item.status)} for ${
                        item.seoTitle
                      }`}
                      className={`mt-6 w-full rounded-2xl px-5 py-3 text-sm font-black transition-all hover:-translate-y-0.5 ${
                        item.status === "Live Now"
                          ? "bg-red-600 text-white shadow-lg shadow-red-600/20 hover:bg-red-700"
                          : item.status === "Upcoming"
                          ? "bg-[#101827] text-white shadow-lg shadow-slate-900/15 hover:bg-slate-800"
                          : "bg-slate-100 text-slate-950 hover:bg-slate-200"
                      }`}
                    >
                      {getButtonText(item.status)}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="bg-white py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-5">
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Everything Students Need
                </p>
                <h2 className="mt-3 text-3xl font-black tracking-[-0.04em] text-slate-950 sm:text-4xl">
                  A Complete Live Learning Experience Beyond Online Classes
                </h2>
                <p className="mt-4 text-sm leading-7 text-slate-500">
                  Mediit Academy live batches help students access schedules,
                  reminders, notes, recordings, quizzes, performance tracking
                  and mentor-led doubt solving in one place.
                </p>

                <button
                  type="button"
                  onClick={goToLogin}
                  aria-label="Start learning with Mediit Academy live batches"
                  className="mt-7 rounded-2xl bg-blue-600 px-7 py-4 text-sm font-black text-white shadow-xl shadow-blue-600/20 transition-all hover:-translate-y-0.5 hover:bg-blue-700"
                >
                  Start Learning
                </button>
              </div>

              <div className="lg:col-span-7">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {features.map((feature) => (
                    <article
                      key={feature.title}
                      title={feature.keywords}
                      className="flex items-start gap-4 rounded-[24px] border border-slate-100 bg-[#f8fbff] p-5"
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-lg font-black text-blue-600 shadow-sm">
                        ✓
                      </span>
                      <div>
                        <h3 className="text-sm font-black text-slate-800">
                          {feature.title}
                        </h3>
                        <p className="mt-2 text-xs leading-5 text-slate-500">
                          {feature.desc}
                        </p>
                        <p className="sr-only">{feature.keywords}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#f8fbff] py-12">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-[36px] bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 p-8 text-white shadow-2xl lg:p-10">
              <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-blue-500/30 blur-3xl" />
              <div className="absolute -bottom-10 left-10 h-44 w-44 rounded-full bg-orange-500/20 blur-3xl" />

              <div className="relative flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-200">
                    Student Dashboard
                  </p>
                  <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] sm:text-4xl">
                    Never Miss Your Live Class Again
                  </h2>

                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/70">
                    Login to manage enrolled classes, reminders, notes,
                    recordings, assignments and test schedules from one student
                    learning dashboard.
                  </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={goToLogin}
                    aria-label="Login to student dashboard"
                    className="rounded-2xl bg-white px-7 py-4 text-sm font-black text-slate-950 transition-all hover:-translate-y-0.5"
                  >
                    Login to Dashboard
                  </button>

                  <button
                    type="button"
                    onClick={scrollToSchedule}
                    aria-label="See live batch schedule"
                    className="rounded-2xl border border-white/15 bg-white/10 px-7 py-4 text-sm font-black text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white/15"
                  >
                    See Schedule
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LiveBatches;